import { useLiveData } from '@toeverything/infra';
import type { Location } from 'history';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import type { Workbench } from '../entities/workbench';

/**
 * This hook binds the workbench to the browser router.
 * It listens to the active view and updates the browser location accordingly.
 * It also listens to the browser location and updates the active view accordingly.
 *
 * The history of the active view and the browser are two different stacks.
 *
 * In the browser, we use browser history as the criterion, and view history is not very important.
 * So our synchronization strategy is as follows:
 *
 * 1. When the active view history changed, we update the browser history, based on the update action.
 *    - If the update action is PUSH, we navigate to the new location.
 *    - If the update action is REPLACE, we replace the current location.
 * 2. When the browser location changed, we update the active view history just in PUSH action.
 * 3. To avoid infinite loop, we add a state to the location to indicate the source of the change.
 */
export function useBindWorkbenchToBrowserRouter(
  workbench: Workbench,
  basename: string
) {
  const navigate = useNavigate();
  const browserLocation = useLocation();
  const browserAction = useNavigationType();

  const view = useLiveData(workbench.activeView$);
  const syncedViewRef = useRef(view);
  const skipNextBrowserToViewSyncRef = useRef(false);
  const browserPositionRef = useRef({
    key: browserLocation.key,
    index: window.history.state?.idx as number | undefined,
  });

  useEffect(() => {
    const previousView = syncedViewRef.current;
    syncedViewRef.current = view;

    if (previousView !== view) {
      const newBrowserLocation = viewLocationToBrowserLocation(
        view.location$.value,
        basename
      );

      skipNextBrowserToViewSyncRef.current = true;

      if (!isSameBrowserLocation(browserLocation, newBrowserLocation)) {
        void navigate(newBrowserLocation, {
          state: 'fromView,' + newBrowserLocation.key,
          replace: true,
        });
      }
    }

    return view.history.listen(update => {
      if (update.action === 'POP' || update.location.state === 'fromBrowser') {
        // This is because the history of view and browser are two different stacks,
        // the POP action cannot be synchronized. Browser-originated updates
        // already have an authoritative URL. Echoing a previous render's
        // location can overwrite a newly resolved admission/navigation.
        return;
      }

      const newBrowserLocation = viewLocationToBrowserLocation(
        update.location,
        basename
      );

      void navigate(newBrowserLocation, {
        state: 'fromView,' + newBrowserLocation.key,
        replace: update.action === 'REPLACE',
      });
    });
  }, [basename, browserLocation, navigate, view]);

  useEffect(() => {
    const previousPosition = browserPositionRef.current;
    const browserIndex = window.history.state?.idx as number | undefined;
    const browserDelta =
      previousPosition.key !== browserLocation.key &&
      typeof browserIndex === 'number' &&
      typeof previousPosition.index === 'number'
        ? browserIndex - previousPosition.index
        : 0;
    browserPositionRef.current = {
      key: browserLocation.key,
      index: browserIndex,
    };
    if (skipNextBrowserToViewSyncRef.current) {
      skipNextBrowserToViewSyncRef.current = false;
      return;
    }

    const newLocation = browserLocationToViewLocation(
      browserLocation,
      basename
    );
    if (newLocation === null) {
      return;
    }
    if (
      typeof newLocation.state === 'string' &&
      newLocation.state.startsWith('fromView')
    ) {
      const fromViewKey = newLocation.state.substring('fromView,'.length);
      if (fromViewKey === view.location$.value.key) {
        return;
      } else {
        const target = view.history.entries.findIndex(
          entry => entry.key === fromViewKey
        );
        if (target !== -1) {
          const now = view.history.index;
          const delta = target - now;
          view.history.go(delta);
          return;
        }
      }
    }
    // A browser entry can retain a fromView key from an earlier active pane
    // or an HMR-restored history. If that key is absent in this view, a POP
    // still means navigation through existing history, not a new PUSH.
    // Resolve the nearest matching URL on the correct side of the current
    // entry; using only the URL without direction would confuse repeated
    // visits and browser Forward with Back.
    if (browserAction === 'POP' && browserDelta !== 0) {
      const now = view.history.index;
      const indices = view.history.entries.flatMap((entry, index) =>
        isSameBrowserLocation(entry, newLocation) &&
        (browserDelta < 0 ? index < now : index > now)
          ? [index]
          : []
      );
      const target = browserDelta < 0 ? indices.at(-1) : indices[0];
      if (target !== undefined) {
        view.history.go(target - now);
        return;
      }
    }
    view.history.push(newLocation, 'fromBrowser');
  }, [basename, browserAction, browserLocation, view]);
}

function browserLocationToViewLocation(
  location: Location,
  basename: string
): Location | null {
  if (!location.pathname.startsWith(basename)) {
    return null;
  }
  return {
    ...location,
    pathname: location.pathname.slice(basename.length),
  };
}

function isSameBrowserLocation(left: Location, right: Location): boolean {
  return (
    left.pathname === right.pathname &&
    left.search === right.search &&
    left.hash === right.hash
  );
}

function viewLocationToBrowserLocation(
  location: Location,
  basename: string
): Location {
  return {
    ...location,
    pathname: `${basename}${location.pathname}`,
  };
}
